import React, { useState, useEffect } from 'react';
import { Filter, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { fetchSegments, type Segment, type SegmentFilters } from '../api/segments';

interface SegmentsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  token: string;
  activeFilters: SegmentFilters;
  onFiltersChange: (filters: SegmentFilters) => void;
}

export default function SegmentsPanel({
  isOpen,
  onClose,
  projectId,
  token,
  activeFilters,
  onFiltersChange,
}: SegmentsPanelProps) {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(false);
  const [localFilters, setLocalFilters] = useState<SegmentFilters>(activeFilters);

  useEffect(() => {
    if (isOpen && projectId) {
      loadSegments();
    }
  }, [isOpen, projectId]);

  useEffect(() => {
    setLocalFilters(activeFilters);
  }, [activeFilters]);

  const loadSegments = async () => {
    try {
      setLoading(true);
      const data = await fetchSegments(projectId, token);
      setSegments(data);
    } catch (error) {
      console.error('Error loading segments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleValueSelect = (key: string, value: string | null) => {
    const newFilters = { ...localFilters };
    if (value === null) {
      delete newFilters[key];
    } else {
      newFilters[key] = value;
    }
    setLocalFilters(newFilters);
  };

  const handleApply = () => {
    onFiltersChange(localFilters);
    onClose();
  };

  const handleClear = () => {
    setLocalFilters({});
    onFiltersChange({});
  };

  const activeFilterCount = Object.keys(localFilters).length;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-[400px] sm:w-[440px] sm:max-w-[440px] flex flex-col p-0">
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b space-y-1">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-muted-foreground" />
            <SheetTitle className="text-lg">Filters</SheetTitle>
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {activeFilterCount} active
              </Badge>
            )}
          </div>
          <SheetDescription>
            Filter data by categorical metadata values
          </SheetDescription>
        </SheetHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : segments.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Filter className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">No filters available yet</p>
              <p className="text-xs mt-2 text-muted-foreground/70">
                Filters will appear as you track events with categorical metadata
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {segments.map((segment) => (
                <div key={`${segment.event_name}-${segment.key}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-semibold text-foreground">
                      {segment.key}
                    </span>
                    <Badge variant="outline" className="text-xs font-normal">
                      {segment.event_name}
                    </Badge>
                  </div>

                  <div className="space-y-1">
                    {/* "All" option */}
                    <button
                      onClick={() => handleValueSelect(segment.key, null)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center justify-between ${
                        !localFilters[segment.key]
                          ? 'bg-primary/10 text-primary font-medium ring-1 ring-primary/20'
                          : 'hover:bg-muted text-muted-foreground'
                      }`}
                    >
                      <span>All values</span>
                      {!localFilters[segment.key] && (
                        <div className="w-2 h-2 rounded-full bg-primary" />
                      )}
                    </button>

                    {/* Value options */}
                    {segment.values.map((v) => (
                      <button
                        key={v.value}
                        onClick={() => handleValueSelect(segment.key, v.value)}
                        className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center justify-between ${
                          localFilters[segment.key] === v.value
                            ? 'bg-primary/10 text-primary font-medium ring-1 ring-primary/20'
                            : 'hover:bg-muted text-muted-foreground'
                        }`}
                      >
                        <span className="truncate mr-2">{v.value}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs opacity-60">
                            {v.percentage.toFixed(0)}%
                          </span>
                          {localFilters[segment.key] === v.value && (
                            <div className="w-2 h-2 rounded-full bg-primary" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 pb-6 border-t bg-muted/30">
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleClear}
              disabled={activeFilterCount === 0}
              className="flex-1"
            >
              Clear all
            </Button>
            <Button
              onClick={handleApply}
              className="flex-1"
            >
              Apply filters
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
